import { openobserveRum } from '@openobserve/browser-rum'
import { openobserveLogs } from '@openobserve/browser-logs'
import packageJson from '../package.json'

// OpenObserve RUM (self-hosted) — replaces the previous Sentry.io integration.
//
// The client token is public-safe: it ships in the browser bundle exactly like
// the old Sentry DSN did. RUM is only enabled when a client token is provided,
// so local/dev builds without the env var simply run without telemetry.

// Determine OpenObserve environment:
// 1. Use explicit VITE_OO_ENVIRONMENT if set (for staging, production, etc.)
// 2. Fall back to Vite MODE ('development' or 'production')
const ooEnvironment = import.meta.env.VITE_OO_ENVIRONMENT || import.meta.env.MODE

const clientToken = import.meta.env.VITE_OO_RUM_CLIENT_TOKEN

// Extract the Supabase origin so RUM injects distributed-tracing headers on our
// API calls (equivalent to Sentry's tracePropagationTargets).
let supabaseOrigin = null
try {
  if (import.meta.env.VITE_SUPABASE_URL) {
    supabaseOrigin = new URL(import.meta.env.VITE_SUPABASE_URL).origin
  }
} catch {
  // Invalid URL - trace propagation to Supabase will be disabled
  if (import.meta.env.DEV) {
    console.warn('[OpenObserve] Invalid VITE_SUPABASE_URL, trace propagation disabled')
  }
}

// Network errors are already handled by the global handlers in main.jsx, so we
// drop them before they reach OpenObserve (was Sentry's beforeSend filter).
const IGNORED_ERROR_FRAGMENTS = ['Failed to fetch', 'NetworkError', 'Load failed']

const isIgnoredMessage = (message = '') =>
  IGNORED_ERROR_FRAGMENTS.some((fragment) => message.includes(fragment))

if (clientToken) {
  const commonConfig = {
    clientToken,
    site: import.meta.env.VITE_OO_SITE || 'rum.taskrdo.com',
    organizationIdentifier: import.meta.env.VITE_OO_ORGANIZATION || 'default',
    service: import.meta.env.VITE_OO_SERVICE || 'focus-app',
    env: ooEnvironment,
    version: packageJson.version, // Track releases for error correlation
    insecureHTTP: false, // Instance is served over HTTPS
    apiVersion: 'v1',
  }

  openobserveRum.init({
    ...commonConfig,
    applicationId: import.meta.env.VITE_OO_APPLICATION_ID || 'focus-app',

    // Capture all sessions initially to establish a baseline (reduce if volume is high)
    sessionSampleRate: 100,
    sessionReplaySampleRate: 100,

    // Performance + interaction tracking (equivalent to Sentry's browser tracing).
    // trackResources is disabled: it emitted ~85% of all RUM events (one per network
    // request / JS module) — dominated by Supabase realtime traffic and, in dev, Vite's
    // unbundled ES modules — with little value for a productivity app.
    trackResources: false,
    trackLongTasks: true,
    trackUserInteractions: true,

    // Propagate trace headers to our Supabase project for distributed tracing
    allowedTracingUrls: supabaseOrigin ? [supabaseOrigin] : [],

    // Session replay is enabled but masks ALL text and inputs. User tasks, goals,
    // and content are never captured in recordings (see PolicyPrivacy.jsx).
    defaultPrivacyLevel: 'mask',

    // Drop network errors already handled by the global handlers in main.jsx
    beforeSend: (event) => {
      if (event.type === 'error' && isIgnoredMessage(event.error?.message)) {
        return false
      }
      return true
    },
  })

  openobserveLogs.init({
    ...commonConfig,

    // Forward console warnings/errors and thrown errors as logs; info/debug stay
    // local (mirrors the old Sentry beforeSendLog filter that dropped info/debug).
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['warn', 'error'],
    sessionSampleRate: 100,

    beforeSend: (log) => !isIgnoredMessage(log.message),
  })

  // Start masked session replay
  openobserveRum.startSessionReplayRecording()
} else if (import.meta.env.DEV) {
  console.info('[OpenObserve] VITE_OO_RUM_CLIENT_TOKEN not set — RUM disabled')
}

/**
 * React 19 error-handler bridge for createRoot's onUncaughtError / onCaughtError.
 * Boundary-caught errors never reach window.onerror, so RUM can't auto-capture
 * them — we forward both caught and uncaught React errors here. No-op when RUM
 * is disabled.
 */
export function reactErrorHandler(error, errorInfo) {
  if (clientToken) {
    openobserveRum.addError(error, { react: errorInfo })
  }
}
